import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, Trash2, RotateCcw, Play, Pause } from 'lucide-react';
import { NODE_META, COLOR_CLASSES, NODE_DEFAULTS, HOLD_MUSIC } from './nodeTypes';
import DepartmentForm from './DepartmentForm';

export const inputCls = 'w-full px-3 py-2 rounded-lg text-sm border bg-black/[0.03] dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:border-teal-400/60';
const muted = 'text-[11px] leading-snug text-slate-500 dark:text-gray-400';

export function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest mb-1.5 text-slate-500 dark:text-gray-400">{label}</label>
      {children}
      {hint && <p className={`mt-1.5 ${muted}`}>{hint}</p>}
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="w-full flex items-center justify-between py-1.5 text-left" data-testid={testid}>
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
      <span className={`w-9 h-5 rounded-full p-0.5 transition-colors ${checked ? 'bg-teal-400' : 'bg-slate-400/40'}`}><span className={`block w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} /></span>
    </button>
  );
}
const num = (v, lo, hi, d) => { const n = parseInt(v, 10); return Number.isNaN(n) ? d : Math.max(lo, Math.min(hi, n)); };

/* ── Start ── */
function StartForm({ d, set, agents, lineLimit, phoneNumber, onPhoneNumberChange }) {
  const [phone, setPhone] = useState(phoneNumber || '');
  useEffect(() => setPhone(phoneNumber || ''), [phoneNumber]);
  const ivrAgents = (agents || []).filter((a) => (a.draft_config?.direction || a.direction) === 'ivr');
  const pick = (id) => { const a = ivrAgents.find((x) => x.agent_id === id); set({ agent_id: id || '', agent_name: a?.name || '' }); };
  const custom = !!d.ai_cap;
  return (<>
    <Field label="IVR agent" hint={ivrAgents.length ? 'The AI answers first on every call: greeting, recording disclosure, then the menu. Its phone number brings callers here.' : 'No IVR agents yet. In AI Agents → Basics, set Call Direction to "IVR / Call Routing".'}>
      <select value={d.agent_id || ''} onChange={(e) => pick(e.target.value)} className={inputCls} data-testid="field-start-ivr-agent">
        <option value="">No IVR agent (keypad menu only)</option>
        {ivrAgents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
      </select>
    </Field>
    <Field label="Max AI calls at once" hint="When the limit is reached, new callers wait in the hold queue before the IVR agent answers.">
      <div className="space-y-1.5 text-xs text-slate-700 dark:text-gray-200">
        <label className="flex items-center gap-2"><input type="radio" checked={!custom} onChange={() => set({ ai_cap: null })} data-testid="start-cap-full" />Use my full line limit{lineLimit ? ` (${lineLimit})` : ''}</label>
        <label className="flex items-center gap-2"><input type="radio" checked={custom} onChange={() => set({ ai_cap: Math.max(1, (lineLimit || 10) - 1) })} data-testid="start-cap-custom" />Set a lower limit</label>
      </div>
      {custom && <input type="number" min={1} max={lineLimit || 999} value={d.ai_cap} onChange={(e) => set({ ai_cap: num(e.target.value, 1, lineLimit || 999, 1) })} className={`${inputCls} mt-2`} data-testid="start-cap-value" />}
    </Field>
    {!d.agent_id && (
      <Field label="Inbound number" hint="Calls to this number enter the flow.">
        <div className="flex gap-2">
          <input value={phone} onChange={(e) => setPhone(e.target.value.trim())} placeholder="+13035551234" className={inputCls} data-testid="field-inbound-number" />
          <button type="button" disabled={!/^\+\d{8,15}$/.test(phone) || phone === phoneNumber} onClick={() => onPhoneNumberChange?.(phone)}
            className="px-3 rounded-lg bg-teal-400 text-[#08090d] text-xs font-bold disabled:opacity-40" data-testid="field-inbound-bind">Bind</button>
        </div>
      </Field>
    )}
  </>);
}

/* ── Hold queue ── */
function QueueForm({ d, set }) {
  const q = { ...NODE_DEFAULTS.queue, ...d };
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const stop = () => { audio.current?.pause(); setPlaying(false); };
  const toggle = () => {
    if (playing) return stop();
    audio.current = new Audio(HOLD_MUSIC.find((m) => m.id === q.hold_music)?.url);
    audio.current.onended = () => setPlaying(false);
    audio.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (<>
    <Field label="Hold music">
      <div className="flex gap-2">
        <select value={q.hold_music} onChange={(e) => { stop(); set({ hold_music: e.target.value }); }} className={inputCls} data-testid="queue-hold-music">
          {HOLD_MUSIC.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <button type="button" onClick={toggle} className="px-3 rounded-lg border border-teal-400/40 text-teal-500 dark:text-teal-300 hover:bg-teal-400/10" title="Preview" data-testid="queue-hold-music-preview">
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>
    </Field>
    <Field label="Minimum hold time (seconds)" hint="Every caller hears hold music for at least this long before being connected. 0 = connect right away when a line is free.">
      <input type="number" min={0} max={120} value={q.min_hold_sec} onChange={(e) => set({ min_hold_sec: num(e.target.value, 0, 120, 0) })} className={inputCls} data-testid="queue-min-hold" />
    </Field>
    <div>
      <Toggle checked={q.announce_position} onChange={(v) => set({ announce_position: v })} label="Say their place in line" testid="queue-announce-position" />
      <Toggle checked={q.announce_wait} onChange={(v) => set({ announce_wait: v })} label="Say the estimated wait time" testid="queue-announce-wait" />
      <Toggle checked={q.callback_offer} onChange={(v) => set({ callback_offer: v })} label='Offer "press 1 to save my place"' testid="queue-callback-offer" />
    </div>
    {q.callback_offer && (
      <Field label="Offer callback after (min)"><input type="number" min={0} max={60} value={q.callback_after_min} onChange={(e) => set({ callback_after_min: num(e.target.value, 0, 60, 0) })} className={inputCls} data-testid="queue-callback-after" /></Field>
    )}
    <Field label="Max wait (min)" hint='After this, callers take "Max wait", or the flow failsafe.'>
      <input type="number" min={1} max={120} value={q.max_wait_min} onChange={(e) => set({ max_wait_min: num(e.target.value, 1, 120, 20) })} className={inputCls} data-testid="queue-max-wait" />
    </Field>
    <p className={muted}>Callers hold here until a line is free, then continue down <b>Line free</b> to the department.</p>
  </>);
}

/* ── Menu ── */
const NAV_RE = /\s*Press \S+ to (?:repeat this menu|return to the main menu)\./gi;
function syncNav(prompt, d) {
  const extra = [];
  if (d.repeat_enabled) extra.push(`Press ${d.repeat_key || '9'} to repeat this menu.`);
  if (d.main_menu_enabled) extra.push(`Press ${d.main_menu_key || '0'} to return to the main menu.`);
  return [(prompt || '').replace(NAV_RE, '').trim(), ...extra].filter(Boolean).join(' ');
}
function MenuForm({ d, set }) {
  const opts = d.options || [];
  const setOpt = (i, k, v) => set({ options: opts.map((o, x) => (x === i ? { ...o, [k]: v } : o)) });
  const reserved = [d.repeat_enabled && (d.repeat_key || '9'), d.main_menu_enabled && (d.main_menu_key || '0')];
  const addOpt = () => {
    const key = '123456789*#'.split('').find((k) => !opts.some((o) => o.key === k) && !reserved.includes(k)) || String(opts.length + 1);
    set({ options: [...opts, { key, label: '', handle_id: `key_${key}_${Date.now().toString(36)}` }] });
  };
  const setNav = (patch) => set({ ...patch, prompt: syncNav(d.prompt, { ...d, ...patch }) });
  return (<>
    <Field label="Spoken prompt" hint="What the IVR agent says. Navigation shortcuts are added to the end automatically.">
      <textarea value={d.prompt || ''} onChange={(e) => set({ prompt: e.target.value })} rows={9} className={`${inputCls} resize-y min-h-[180px] leading-relaxed`} placeholder="For sales, press 1 or say sales…" data-testid="menu-prompt" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Timeout (sec)"><input type="number" min={3} max={30} value={d.timeout || 10} onChange={(e) => set({ timeout: num(e.target.value, 3, 30, 10) })} className={inputCls} data-testid="menu-timeout" /></Field>
      <Field label="Tries before failsafe"><input type="number" min={1} max={6} value={d.max_retries || 3} onChange={(e) => set({ max_retries: num(e.target.value, 1, 6, 3) })} className={inputCls} data-testid="menu-max-retries" /></Field>
    </div>
    <Field label="Branch options">
      <div className="space-y-2">
        {opts.map((o, i) => (
          <div key={o.handle_id || i} className="flex items-center gap-2">
            <input value={o.key || ''} onChange={(e) => setOpt(i, 'key', e.target.value.slice(0, 1))} className={`${inputCls} !w-11 shrink-0 text-center`} data-testid={`menu-option-key-${i}`} />
            <input value={o.label || ''} onChange={(e) => setOpt(i, 'label', e.target.value)} placeholder="Label (e.g. Sales)" className={`${inputCls} flex-1 min-w-0`} data-testid={`menu-option-label-${i}`} />
            <button type="button" onClick={() => setOpt(i, 'announce', o.announce === false)} title="IVR agent says 'Connecting you to … now' before routing"
              className={`px-2 py-1.5 rounded text-[10px] font-semibold border ${o.announce === false ? 'border-slate-300 dark:border-white/10 text-slate-500' : 'border-teal-400/40 text-teal-600 dark:text-teal-300 bg-teal-400/10'}`}
              data-testid={`menu-option-announce-${i}`}>{o.announce === false ? 'Silent' : 'Announce'}</button>
            <button type="button" onClick={() => set({ options: opts.filter((_, x) => x !== i) })} className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded" data-testid={`menu-option-remove-${i}`}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" onClick={addOpt} className="w-full px-3 py-2 rounded-lg border border-dashed border-slate-300 dark:border-white/15 text-[11px] text-slate-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5" data-testid="menu-add-option"><Plus size={11} className="inline mr-1" />Add option</button>
      </div>
    </Field>
    <div className="rounded-lg border border-slate-200 dark:border-white/10 p-3 space-y-1">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">Navigation shortcuts</div>
      <Toggle label={`Press ${d.repeat_key || '9'} to repeat this menu`} checked={!!d.repeat_enabled} onChange={(v) => setNav({ repeat_enabled: v })} testid="menu-repeat-toggle" />
      {d.repeat_enabled && <input value={d.repeat_key || '9'} onChange={(e) => setNav({ repeat_key: e.target.value.slice(0, 1) })} className={`${inputCls} !w-16 text-center`} data-testid="menu-repeat-key" />}
      <Toggle label={`Press ${d.main_menu_key || '0'} to return to main menu`} checked={!!d.main_menu_enabled} onChange={(v) => setNav({ main_menu_enabled: v })} testid="menu-mainmenu-toggle" />
      {d.main_menu_enabled && <input value={d.main_menu_key || '0'} onChange={(e) => setNav({ main_menu_key: e.target.value.slice(0, 1) })} className={`${inputCls} !w-16 text-center`} data-testid="menu-mainmenu-key" />}
    </div>
  </>);
}

function renderForm(type, d, set, ctx) {
  switch (type) {
    case 'start': return <StartForm d={d} set={set} {...ctx} />;
    case 'menu': return <MenuForm d={d} set={set} />;
    case 'queue': return <QueueForm d={d} set={set} />;
    case 'department': return <DepartmentForm d={d} set={(k, v) => set({ [k]: v })} inputCls={inputCls} Field={Field} phoneNumbers={ctx.phoneNumbers} />;
    case 'tag_filter': return <Field label="LeadConnector contact tag"><input value={d.tag || ''} onChange={(e) => set({ tag: e.target.value })} placeholder="e.g. lead-magnet" className={inputCls} data-testid="field-tag" /></Field>;
    case 'contact_filter': return <p className={muted}>Branches on whether the caller's phone matches an existing LeadConnector contact. No setup needed.</p>;
    case 'hangup': return <Field label="Optional farewell"><input value={d.farewell || ''} onChange={(e) => set({ farewell: e.target.value })} placeholder="Goodbye!" className={inputCls} data-testid="field-farewell" /></Field>;
    default: return <p className={muted}>This step type is no longer used. Delete it and use a Department instead.</p>;
  }
}

/* Right-rail step editor. Every edit applies to the canvas instantly and auto-saves. */
export default function NodeConfigDrawer({ node, onPatch, onDelete, onClose, ...ctx }) {
  const meta = NODE_META[node.type] || { label: node.type, color: 'sky' };
  const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.sky;
  const d = node.data || {};
  const set = (patch) => onPatch(node.id, patch);
  return (
    <aside className="w-[380px] flex flex-col border-l" style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }} data-testid={`node-config-drawer-${node.type}`}>
      <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--vox-chrome-border)' }}>
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider border ${c.bg} ${c.text} ${c.border}`}>{meta.label.toUpperCase()}</span>
        <input value={d.label || ''} onChange={(e) => set({ label: e.target.value })} placeholder="Step label (optional)"
          className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none text-slate-900 dark:text-white placeholder:text-slate-400" data-testid="drawer-label" />
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900 dark:hover:text-white" data-testid="drawer-close"><X size={16} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">{renderForm(node.type, d, set, ctx)}</div>
      <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: 'var(--vox-chrome-border)' }}>
        {node.type !== 'start' && (
          <button onClick={onDelete} className="px-3 py-2 rounded-lg text-rose-500 dark:text-rose-300 border border-rose-400/30 text-xs hover:bg-rose-500/10" data-testid="drawer-delete">
            <Trash2 size={12} className="inline mr-1.5" />Delete
          </button>
        )}
        <button onClick={() => onPatch(node.id, { ...NODE_DEFAULTS[node.type] }, true)} className="px-3 py-2 rounded-lg text-xs border border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5" data-testid="drawer-reset">
          <RotateCcw size={12} className="inline mr-1.5" />Reset
        </button>
        <span className="ml-auto text-[10px] text-slate-400">Changes save automatically</span>
      </div>
    </aside>
  );
}
